import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Card, Tag, Typography, Space, Badge } from 'antd';
import {
  CheckCircleOutlined,
  LoadingOutlined,
  RightCircleOutlined,
  PlusSquareOutlined,
  MinusSquareOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getNodeStatusColor } from '../../utils/graphLayout';

const { Text } = Typography;

interface StepNodeData {
  label: string;
  status: string;
  time: string;
  endTime?: string;
  namespace: string;
  jobId: string;
  spanId?: string;
  traceId: string;
  details?: string;
  hasChildren?: boolean;
  isExpanded?: boolean;
  childCount?: number;
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  PROCESS_STEP_START: <RightCircleOutlined />,
  PROCESS_STEP_END: <CheckCircleOutlined />,
  INFO: <RightCircleOutlined />,
  DEBUG: <RightCircleOutlined />,
  WARN: <LoadingOutlined />,
  ERROR: <LoadingOutlined />,
  FATAL: <LoadingOutlined />,
};

function StepNodeComponent({ data, selected }: NodeProps<StepNodeData>) {
  const borderColor = getNodeStatusColor(data.status);
  const icon = STATUS_ICONS[data.status] || <LoadingOutlined />;
  const hasChildren = data.hasChildren;
  const isExpanded = data.isExpanded;
  const childCount = data.childCount || 0;

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <Card
        size="small"
        style={{
          width: 200,
          borderColor: selected ? '#1890ff' : borderColor,
          borderWidth: 2,
          borderStyle: 'dashed',
          boxShadow: selected ? '0 0 0 2px rgba(24, 144, 255, 0.2)' : undefined,
          cursor: 'pointer',
        }}
        styles={{ body: { padding: 10 } }}
      >
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Space>
              <span style={{ color: borderColor }}>{icon}</span>
              <Text strong style={{ fontSize: 12 }} ellipsis>
                {data.details || 'Step'}
              </Text>
            </Space>
            {hasChildren && (
              <Badge count={childCount} size="small" style={{ backgroundColor: '#8c8c8c' }}>
                {isExpanded ? (
                  <MinusSquareOutlined style={{ fontSize: 12, color: '#1890ff' }} />
                ) : (
                  <PlusSquareOutlined style={{ fontSize: 12, color: '#1890ff' }} />
                )}
              </Badge>
            )}
          </Space>

          <Tag
            color={borderColor}
            style={{ margin: 0, fontSize: 10 }}
          >
            {data.status.replace(/_/g, ' ')}
          </Tag>

          {data.spanId && (
            <Text
              type="secondary"
              style={{ fontSize: 10 }}
              ellipsis={{ tooltip: data.spanId }}
            >
              Span: {data.spanId.substring(0, 8)}...
            </Text>
          )}

          <Text type="secondary" style={{ fontSize: 10 }}>
            {dayjs(data.time).format('HH:mm:ss.SSS')}
            {data.endTime && ` → ${dayjs(data.endTime).format('HH:mm:ss.SSS')}`}
          </Text>
        </Space>
      </Card>
      <Handle type="source" position={Position.Bottom} />
    </>
  );
}

export const StepNode = memo(StepNodeComponent);
